import { useTranslation } from "@redotech/redo-i18n";
import CubeIcon from "@redotech/redo-icons/cube-01.svg";
import HelpCircleIcon from "@redotech/redo-icons/help-circle.svg";
import ScalesIcon from "@redotech/redo-icons/scales-01.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import FlowArrowIcon from "./flow-arrow.svg";
import PackageBoxIcon from "./package-box.svg";
import PrintedLabelIcon from "./printed-label.svg";

const SimpleRateTooltipContent = memo(function SimpleRateTooltipContent() {
  const { t } = useTranslation();
  return (
    <Flex dir="column" gap="lg">
      <Text fontSize="sm" fontWeight="semibold" textColor="white">
        UPS Simple Rate
      </Text>
      <Flex align="center" dir="row" gap="xl" justify="center">
        <Flex align="center" dir="column" gap="sm">
          <PackageBoxIcon />
          <Text fontSize="xs" textAlign="center" textColor="white">
            {t("Actual weight")}
          </Text>
        </Flex>
        <FlowArrowIcon />
        <Flex align="center" dir="column" gap="sm">
          <PrintedLabelIcon />
          <Flex align="center" dir="column" gap="none">
            <Text fontSize="xs" textAlign="center" textColor="white">
              {t("Printed weight")}
            </Text>
            <Text fontSize="xs" textAlign="center" textColor="white">
              {t("may differ")}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Text fontSize="xs" textColor="tooltip-supporting-text">
        {t(
          "UPS Simple Rate uses flat pricing based on package size. The weight printed on the label may not match the package’s actual weight. This is expected.",
        )}
      </Text>
      <Divider />
      <Flex align="stretch" dir="row">
        <Flex align="center" dir="row" gap="sm" grow={1} pr="lg">
          <ScalesIcon height={28} width={28} />
          <Flex dir="column" gap="xxs">
            <Text fontSize="xs" textColor="tooltip-supporting-text">
              {t("Max weight")}
            </Text>
            <Text fontSize="sm" fontWeight="medium" textColor="white">
              50 lb
            </Text>
          </Flex>
        </Flex>
        <Flex
          align="center"
          borderColor="primary"
          borderLeftWidth="1px"
          borderStyle="solid"
          dir="row"
          gap="sm"
          grow={1}
          pl="lg"
        >
          <CubeIcon height={28} width={28} />
          <Flex dir="column" gap="xxs">
            <Text fontSize="xs" textColor="tooltip-supporting-text">
              {t("Max volume")}
            </Text>
            <Text fontSize="sm" fontWeight="medium" textColor="white">
              1 cu ft
            </Text>
            <Text fontSize="xs" textColor="tooltip-supporting-text">
              (1,728 cu in)
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Divider />
      <Text
        fontSize="xxs"
        textAlign="center"
        textColor="tooltip-supporting-text"
      >
        {t("Larger or heavier packages use standard UPS rates.")}
      </Text>
    </Flex>
  );
});

/** UPS Simple Rate badge, with the tooltip that explains the printed weight. */
export const SimpleRateBadge = memo(function SimpleRateBadge() {
  return (
    <Tooltip
      arrow
      leftAlign
      paddingOverride="16px"
      title={<SimpleRateTooltipContent />}
    >
      <Flex w="fit-content">
        <RedoBadge
          iconTrailing={{ type: "icon", Icon: HelpCircleIcon }}
          size="xs"
          text="Simple Rate"
        />
      </Flex>
    </Tooltip>
  );
});
